import {
  Stack,
  IconButton,
  CloseIcon,
  BinIcon,
  Button,
  AddIcon,
  NumberInput,
  Alert,
  TimeInput
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
// For now we keep it like this until Mantine 8.0 is available for us to use an input time with a specific hour format (and not dependant on the computer format)
import { isValidTime } from '@spa/components/FormFields/helpers'
import { cva } from 'class-variance-authority'
import moment from 'moment'
import type { ChangeEvent, FC, MouseEvent } from 'react'
import React, { useState } from 'react'

import styles from './FieldTimeRangeDuration.module.css'

const FieldTimeRangeDurationCVA = {
  root: cva([''], {
    variants: {
      isSecond: { true: 'ml-2' },
      highlightError: {
        true: 'border-semantic-red-400'
      }
    }
  })
}

type Value = {
  id?: number
  plannedDuration?: number
  timeRange?: string[]
}

export type FieldTimeRangeDurationProps = {
  inputName?: string
  disabled?: boolean
  error?: string
  timeRangeConstraint?: {
    start: string
    end: string
  }
  highlightError?: boolean
  disableTimeRange?: boolean
  isManager?: boolean
  value?: Value
  onRemove?: (evt: MouseEvent<HTMLButtonElement>) => void
  onChange?: (newValue: Value) => void
  dataTestId?: string
}

export const FieldTimeRangeDuration: FC<FieldTimeRangeDurationProps> = ({
  inputName,
  value = { plannedDuration: 0, timeRange: ['', ''] },
  timeRangeConstraint,
  disabled,
  error,
  highlightError,
  disableTimeRange,
  onChange,
  onRemove,
  isManager = false,
  dataTestId = 'field-time-range-duration'
}) => {
  const { t } = usePlanningFormTranslation()
  const [displayTimeRange, setDisplayTimeRange] = useState(
    (value?.timeRange?.length ?? 0) > 0
  )

  const { plannedDuration = 0, timeRange = ['', ''], ...restValue } = value

  const toggleDisplayTimeRange = () => {
    if (displayTimeRange && onChange) {
      onChange({ plannedDuration, timeRange: ['', ''] })
    }
    setDisplayTimeRange(!displayTimeRange)
  }

  /**
   * Compute endTime of [timeRange] given startTime and duration
   */
  const computeTimeRangeWithDuration = (
    startRange: string,
    newDuration: number
  ): [string, string] => {
    const startTime = moment(startRange, 'HH:mm')
    const endTime = startTime.add(newDuration, 'minutes')
    return [startRange, endTime.format('HH:mm')]
  }

  /**
   * Compute [timeRange] based on duration updates
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newDuration = e.target.valueAsNumber
    const [startRange] = timeRange

    if (!onChange) {
      return
    }
    let newData: Value
    switch (true) {
      case !disableTimeRange && newDuration && isValidTime(startRange): {
        newData = {
          ...restValue,
          plannedDuration: newDuration,
          timeRange: computeTimeRangeWithDuration(startRange, newDuration)
        }
        break
      }
      case !disableTimeRange && (!newDuration || newDuration === 0): {
        newData = {
          ...restValue,
          plannedDuration: newDuration || 0,
          timeRange: startRange ? [startRange, ''] : []
        }
        break
      }
      default: {
        newData = {
          ...restValue,
          plannedDuration: newDuration,
          timeRange: displayTimeRange ? timeRange : []
        }
        break
      }
    }
    return onChange(newData)
  }

  /**
   * Compute [timeRange:end] based on [timeRange:start] updates
   */
  const handleTimeRangeChange = (newFirstValue: string) => {
    const [newStartRange] = [newFirstValue, timeRange[1]]

    if (onChange) {
      if (isValidTime(newStartRange) && !!plannedDuration) {
        return onChange({
          ...restValue,
          plannedDuration,
          timeRange: computeTimeRangeWithDuration(
            newStartRange,
            plannedDuration
          )
        })
      }
      onChange({
        ...restValue,
        plannedDuration,
        timeRange: [newStartRange, '']
      })
    }
  }

  /**
   * Compute max duration based on fieldTimeRange values
   */
  const getMaxDuration = () => {
    if (!timeRangeConstraint) {
      return Infinity
    }

    const momentEnd = moment(timeRangeConstraint.end)
    const momentStart = moment(timeRangeConstraint.start)

    if (momentEnd.isBefore(momentStart)) {
      momentEnd.add(1, 'day')
    }

    return moment.duration(momentEnd.diff(momentStart)).asMinutes()
  }

  return (
    <div className={styles.GroupBreakRow} data-testid={dataTestId}>
      <div className={styles.BreakRow}>
        <div className={styles.ContentContainer}>
          <NumberInput
            debounceDelay='medium'
            max={
              Number.isFinite(getMaxDuration()) ? getMaxDuration() : undefined
            }
            min={0}
            onChange={handleInputChange}
            name={inputName}
            size='s'
            step={1}
            suffix='min'
            disabled={disabled}
            showArrows
            value={plannedDuration.toString()}
            classNames={{
              inner: 'w-20',
              root: !!error || highlightError ? 'border-semantic-red-400' : '',
              input: 'w-10'
            }}
            data-testid='field-duration-time-range'
          />
          {!disableTimeRange && displayTimeRange && (
            <div className={styles.TimeRange}>
              <TimeInput
                key='input-time-0'
                defaultValue={timeRange[0]}
                onChange={e => handleTimeRangeChange(e.target.value)}
                disabled={disabled}
                name='input-time-0'
                data-testid='input-time-0'
                leftSection={t('component.fieldTimeRange.from')}
                classNames={{
                  root: FieldTimeRangeDurationCVA.root({
                    highlightError: !!error || highlightError
                  })
                }}
              />
              <TimeInput
                key='input-time-1'
                value={timeRange[1]}
                disabled
                name='input-time-1'
                data-testid='input-time-1'
                leftSection={t('component.fieldTimeRange.to')}
                classNames={{
                  root: FieldTimeRangeDurationCVA.root({
                    highlightError: !!error || highlightError,
                    isSecond: true
                  })
                }}
              />
            </div>
          )}

          {!disableTimeRange && !displayTimeRange && !disabled && (
            <Button
              intent='neutral-dark'
              variant='tertiary'
              leftIcon={<AddIcon />}
              onClick={toggleDisplayTimeRange}
              data-testid='add'
              className='ml-3 w-full'
            >
              {t('component.fieldTimeRange.showBreak')}
            </Button>
          )}
        </div>

        <Stack gap={2}>
          {!disableTimeRange && displayTimeRange && (
            <IconButton
              icon={<CloseIcon />}
              disabled={disabled}
              onClick={toggleDisplayTimeRange}
              variant='tertiary'
              intent='neutral-dark'
              data-testid='close'
            />
          )}
          {onRemove && !disabled && isManager && (
            <IconButton
              icon={<BinIcon />}
              onClick={onRemove}
              data-testid='delete'
              variant='tertiary'
              intent='danger'
            />
          )}
        </Stack>
      </div>
      <div>
        {error && (
          <Alert intent='semantic.red' className='my-2'>
            {error}
          </Alert>
        )}
      </div>
    </div>
  )
}
